import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Copy, Check, KeySquare, PlugZap, Lock, ArrowUpRight } from 'lucide-react';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { Modal } from '../../components/common/Modal';
import { artisanIntegrationService } from '../../services/artisanIntegrationService';
import type { ArtisanApiKey } from '../../services/artisanIntegrationService';
import { useLanguage } from '../../features/language/useLanguage';
import { useSubscription } from '../../hooks/useSubscription';

interface CompanyArtisanIntegrationProps {
  readOnly?: boolean;
}

export const CompanyArtisanIntegration: React.FC<CompanyArtisanIntegrationProps> = ({ readOnly = false }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { isFree } = useSubscription();
  const statusLabel = (status: string) => t(`settingsArtisan.status.${status}`, undefined, status);
  const [keys, setKeys] = useState<ArtisanApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewKey, setShowNewKey] = useState<string | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // PRD 76: Free tier cannot create Artisan API keys. Click on "Generate New
  // Key" opens an upgrade modal instead of the create form.
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const handleGenerateClick = () => {
    if (isFree) {
      setShowUpgradeModal(true);
      return;
    }
    setIsCreating(true);
  };

  const fetchKeys = useCallback(async () => {
    try {
      const data = await artisanIntegrationService.getKeys();
      setKeys(data);
    } catch (err) {
      console.error('Failed to fetch artisan integration keys:', err);
      setError(t('settingsArtisan.errors.loadKeys'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchKeys();
  }, [fetchKeys]);

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;

    try {
      const response = await artisanIntegrationService.createKey(newKeyName);
      setKeys([response.api_key, ...keys]);
      setShowNewKey(response.key);
      setNewKeyName('');
      setIsCreating(false);
    } catch (err) {
      console.error('Failed to create artisan integration key:', err);
      setError(t('settingsArtisan.errors.createKey'));
    }
  };

  const handleRevokeKey = async (id: string) => {
    if (!window.confirm(t('settingsArtisan.confirmRevoke'))) {
      return;
    }

    try {
      await artisanIntegrationService.revokeKey(id);
      setKeys(keys.map((key) => (key.id === id ? { ...key, status: 'revoked' } : key)));
    } catch (err) {
      console.error('Failed to revoke artisan integration key:', err);
      setError(t('settingsArtisan.errors.revokeKey'));
    }
  };

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value);
    setCopied(value);
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="bg-white shadow sm:rounded-lg" data-test-id="artisan-integration-settings-container">
      <div className="px-4 py-5 sm:p-6">
        <div className="md:flex md:items-center md:justify-between">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center gap-2">
              <PlugZap className="h-5 w-5 text-gray-500" />
              {t('settingsArtisan.title')}
            </h3>
            <div className="mt-2 max-w-xl text-sm text-gray-500">
              <p>{t('settingsArtisan.subtitle')}</p>
            </div>
          </div>
          {!readOnly && (
            <div className="mt-4 md:mt-0">
              <Button onClick={handleGenerateClick} disabled={isCreating} data-test-id="artisan-generate-key-button">
                {isFree ? <Lock className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                {t('settingsArtisan.actions.generateNewKey')}
              </Button>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-md border border-red-200" data-test-id="artisan-key-error-message">
            {error}
          </div>
        )}

        {isCreating && !showNewKey && (
          <div className="mt-6 p-4 bg-gray-50 rounded-md border border-gray-200" data-test-id="artisan-create-key-form">
            <h4 className="text-sm font-medium text-gray-900 mb-2">{t('settingsArtisan.createForm.title')}</h4>
            <div className="flex gap-4">
              <input
                data-test-id="artisan-new-key-name-input"
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder={t('settingsArtisan.createForm.namePlaceholder')}
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm p-2 border"
              />
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => setIsCreating(false)} data-test-id="artisan-cancel-create-key-button">{t('auth.common.cancel')}</Button>
                <Button onClick={handleCreateKey} disabled={!newKeyName.trim()} data-test-id="artisan-confirm-create-key-button">{t('settingsArtisan.actions.create')}</Button>
              </div>
            </div>
          </div>
        )}

        {showNewKey && (
          <div className="mt-6 p-4 bg-green-50 rounded-md border border-green-200" data-test-id="artisan-new-key-display">
            <h4 className="text-sm font-medium text-green-800">{t('settingsArtisan.newKey.title')}</h4>
            <div className="mt-2 text-sm text-green-700">
              <p>{t('settingsArtisan.newKey.description')}</p>
            </div>
            <div className="mt-3">
              <div
                className="flex items-center gap-2 bg-white p-2 rounded border border-green-200 font-mono text-sm text-gray-800"
                data-test-id="artisan-new-key-value"
                title={t('settingsArtisan.newKey.copyTooltip')}
              >
                <span className="flex-1 break-all">{showNewKey}</span>
                <button
                  type="button"
                  onClick={() => copyToClipboard(showNewKey)}
                  className="inline-flex items-center gap-1 rounded-md border border-primary-800 bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700"
                  data-test-id="artisan-copy-new-key-button"
                >
                  {copied === showNewKey ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied === showNewKey ? t('settingsArtisan.actions.copied') : t('settingsArtisan.actions.copyKey')}
                </button>
              </div>
            </div>
            <div className="mt-4">
              <Button size="sm" onClick={() => setShowNewKey(null)} data-test-id="artisan-dismiss-new-key-button">{t('settingsArtisan.actions.done')}</Button>
            </div>
          </div>
        )}

        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-300" data-test-id="artisan-keys-table">
                <thead>
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">{t('settingsArtisan.table.name')}</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('settingsArtisan.table.keyPrefix')}</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('settingsArtisan.table.created')}</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('settingsArtisan.table.lastUsed')}</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">{t('settingsArtisan.table.status')}</th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                      <span className="sr-only">{t('settingsArtisan.table.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {keys.map((key) => (
                    <tr key={key.id} className={key.status === 'revoked' ? 'opacity-50' : ''} data-test-id={`artisan-key-row-${key.id}`}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                        <div className="flex items-center gap-2">
                          <KeySquare className="h-4 w-4 text-gray-400" />
                          {key.name}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-mono">{key.prefix}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{new Date(key.created_at).toLocaleDateString()}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : t('settingsArtisan.values.none')}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                          key.status === 'active'
                            ? 'bg-green-50 text-green-700 ring-green-600/20'
                            : 'bg-red-50 text-red-700 ring-red-600/20'
                        }`} data-test-id={`artisan-key-status-${key.id}`}>
                          {statusLabel(key.status)}
                        </span>
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                        {key.status === 'active' && !readOnly && (
                          <button
                            data-test-id={`artisan-revoke-key-button-${key.id}`}
                            onClick={() => handleRevokeKey(key.id)}
                            className="text-red-600 hover:text-red-900 flex items-center gap-1 ml-auto"
                          >
                            <Trash2 className="h-4 w-4" />
                            {t('settingsArtisan.actions.revoke')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title={t('subscription.upgradeRequired.artisanIntegration.title')}
        maxWidth="md"
      >
        <div className="space-y-4" data-test-id="artisan-upgrade-required-modal-body">
          <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <Lock className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
            <p className="text-sm text-amber-800">
              {t('subscription.upgradeRequired.artisanIntegration.body')}
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowUpgradeModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              data-test-id="artisan-upgrade-modal-dismiss"
            >
              {t('auth.common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowUpgradeModal(false);
                navigate('/settings/subscription');
              }}
              data-test-id="artisan-upgrade-modal-cta"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              {t('subscription.upgradeRequired.artisanIntegration.cta')}
              <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
